import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { authApi, paymentsInfrastructureApi } from '@/lib/pillarApi';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type {
    ActiveRoleAssignmentResponse,
    BusinessDetails,
    LogicalTerminalListItem,
    LogicalTerminalSearchPageResponse,
    MerchantAccountSearchListItem,
    MerchantAccountSearchPageResponse,
    ProcessorListItem,
    ProcessorSearchPageResponse,
    TerminalDeviceListItem,
    TerminalDeviceSearchPageResponse,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';

function getMerchantDisplayName(businessDetails: BusinessDetails | undefined): string | null {
    const dba = businessDetails?.dba?.trim();
    if (dba !== undefined && dba.length > 0) {
        return dba;
    }

    const legalEntityName = businessDetails?.legalEntityName.trim();
    if (legalEntityName !== undefined && legalEntityName.length > 0) {
        return legalEntityName;
    }

    return null;
}

function parseOpaqueCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = z.string().trim().min(1).safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

function DuplicateControls(props: {
    hasDuplicates: boolean;
    duplicatesOnly: boolean;
    onToggleDuplicatesOnly: () => void;
}): ReactElement | null {
    if (!props.hasDuplicates) {
        return null;
    }
    return (
        <div className="mb-3 flex items-center justify-between rounded border border-red-300 bg-red-50 p-3">
            <p className="text-sm text-red-700">
                Duplicate IDs detected in your current access scope.
            </p>
            <Button variant="destructive" size="sm" onClick={props.onToggleDuplicatesOnly}>
                {props.duplicatesOnly ? 'Show all records' : 'Show duplicates only'}
            </Button>
        </div>
    );
}

function useActiveRoleAssignment(): {
    activeRoleAssignment: ActiveRoleAssignmentResponse | null | undefined;
    isLoading: boolean;
} {
    const queryResult = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    return {
        activeRoleAssignment: queryResult.data,
        isLoading: queryResult.isLoading,
    };
}

export function ProcessorsListPage() {
    const navigate = useNavigate();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['paymentsInfrastructure', 'processors', `orgs=${organizationListsVersion}`],
        [organizationListsVersion]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignment ?? null
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<ProcessorListItem, number, ReadonlyArray<string>>
            pageTitle="Processors"
            cardTitle="Processors"
            queryPlaceholder="Search processors..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: ProcessorSearchPageResponse =
                    await paymentsInfrastructureApi.searchProcessors({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                return { items: response.processors, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.number().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected numeric cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading processors...',
                emptyLabel: 'No processors found.',
                endOfListLabel: 'End of processors.',
                genericErrorLabel: 'Failed to load processors.',
            }}
            headerActions={
                canCreate ? (
                    <Button onClick={() => navigate('/payments-infrastructure/processors/create')}>
                        Create Processor
                    </Button>
                ) : undefined
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((processor: ProcessorListItem) => (
                        <button
                            key={processor.processor_name}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() =>
                                navigate(
                                    `/payments-infrastructure/processors/${encodeURIComponent(processor.processor_name)}`
                                )
                            }
                        >
                            <h3 className="font-semibold">{processor.processor_name}</h3>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function MerchantAccountsListPage() {
    const navigate = useNavigate();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'merchantAccounts',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
        ],
        [duplicatesOnly, organizationListsVersion]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignment ?? null
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<MerchantAccountSearchListItem, string, ReadonlyArray<string>>
            pageTitle="Merchant Accounts"
            cardTitle="Merchant Accounts"
            queryPlaceholder="Search merchant accounts..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: MerchantAccountSearchPageResponse =
                    await paymentsInfrastructureApi.searchMerchantAccounts({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.merchant_accounts, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading merchant accounts...',
                emptyLabel: 'No merchant accounts found.',
                endOfListLabel: 'End of merchant accounts.',
                genericErrorLabel: 'Failed to load merchant accounts.',
            }}
            headerActions={
                canCreate ? (
                    <Button onClick={() => navigate('/payments-infrastructure/merchant-accounts/create')}>
                        Create Merchant Account
                    </Button>
                ) : undefined
            }
            controls={
                <DuplicateControls
                    hasDuplicates={hasDuplicatesInScope}
                    duplicatesOnly={duplicatesOnly}
                    onToggleDuplicatesOnly={() => {
                        setDuplicatesOnly((currentValue: boolean) => !currentValue);
                    }}
                />
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((merchantAccount: MerchantAccountSearchListItem) => (
                        <button
                            key={merchantAccount.merchant_account_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() =>
                                navigate(
                                    `/payments-infrastructure/merchant-accounts/${merchantAccount.merchant_account_uuid}`
                                )
                            }
                        >
                            {getMerchantDisplayName(merchantAccount.businessDetails) !== null && (
                                <p className="text-sm text-muted-foreground">
                                    {getMerchantDisplayName(merchantAccount.businessDetails)}
                                </p>
                            )}
                            <h3 className="font-semibold">
                                {merchantAccount.processor_name} / {merchantAccount.processor_mid}
                            </h3>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function LogicalTerminalsListPage() {
    const navigate = useNavigate();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'logicalTerminals',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
        ],
        [duplicatesOnly, organizationListsVersion]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignment ?? null
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<LogicalTerminalListItem, string, ReadonlyArray<string>>
            pageTitle="Logical Terminals"
            cardTitle="Logical Terminals"
            queryPlaceholder="Search logical terminals..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: LogicalTerminalSearchPageResponse =
                    await paymentsInfrastructureApi.searchLogicalTerminals({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.logical_terminals, pagination: response.pagination };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading logical terminals...',
                emptyLabel: 'No logical terminals found.',
                endOfListLabel: 'End of logical terminals.',
                genericErrorLabel: 'Failed to load logical terminals.',
            }}
            headerActions={
                canCreate ? (
                    <Button onClick={() => navigate('/payments-infrastructure/logical-terminals/create')}>
                        Create Logical Terminal
                    </Button>
                ) : undefined
            }
            controls={
                <DuplicateControls
                    hasDuplicates={hasDuplicatesInScope}
                    duplicatesOnly={duplicatesOnly}
                    onToggleDuplicatesOnly={() => {
                        setDuplicatesOnly((currentValue: boolean) => !currentValue);
                    }}
                />
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((logicalTerminal: LogicalTerminalListItem) => (
                        <button
                            key={logicalTerminal.logical_terminal_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() =>
                                navigate(
                                    `/payments-infrastructure/logical-terminals/${logicalTerminal.logical_terminal_uuid}`
                                )
                            }
                        >
                            {getMerchantDisplayName(logicalTerminal.businessDetails) !== null && (
                                <p className="text-sm text-muted-foreground">
                                    {getMerchantDisplayName(logicalTerminal.businessDetails)}
                                </p>
                            )}
                            <h3 className="font-semibold">
                                {logicalTerminal.processor_name} / {logicalTerminal.processor_mid} /{' '}
                                {logicalTerminal.processor_tid}
                            </h3>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function TerminalDevicesListPage() {
    const navigate = useNavigate();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'terminalDevices',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
        ],
        [duplicatesOnly, organizationListsVersion]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignment ?? null
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<TerminalDeviceListItem, string, ReadonlyArray<string>>
            pageTitle="Terminal Devices"
            cardTitle="Terminal Devices"
            queryPlaceholder="Search terminal devices..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: TerminalDeviceSearchPageResponse =
                    await paymentsInfrastructureApi.searchTerminalDevices({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.terminal_devices, pagination: response.pagination };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading terminal devices...',
                emptyLabel: 'No terminal devices found.',
                endOfListLabel: 'End of terminal devices.',
                genericErrorLabel: 'Failed to load terminal devices.',
            }}
            headerActions={
                canCreate ? (
                    <Button onClick={() => navigate('/payments-infrastructure/terminal-devices/create')}>
                        Create Terminal Device
                    </Button>
                ) : undefined
            }
            controls={
                <DuplicateControls
                    hasDuplicates={hasDuplicatesInScope}
                    duplicatesOnly={duplicatesOnly}
                    onToggleDuplicatesOnly={() => {
                        setDuplicatesOnly((currentValue: boolean) => !currentValue);
                    }}
                />
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((terminalDevice: TerminalDeviceListItem) => (
                        <button
                            key={terminalDevice.terminal_device_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() =>
                                navigate(
                                    `/payments-infrastructure/terminal-devices/${terminalDevice.terminal_device_uuid}`
                                )
                            }
                        >
                            {getMerchantDisplayName(terminalDevice.businessDetails) !== null && (
                                <p className="text-sm text-muted-foreground">
                                    {getMerchantDisplayName(terminalDevice.businessDetails)}
                                </p>
                            )}
                            <h3 className="font-semibold">
                                {terminalDevice.processor_name} / {terminalDevice.processor_mid} /{' '}
                                {terminalDevice.processor_tid} / {terminalDevice.processor_device_id}
                            </h3>
                            <div className="mt-1">
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        terminalDevice.is_virtual
                                            ? 'bg-gray-100 text-gray-700'
                                            : 'bg-blue-100 text-blue-700'
                                    }`}
                                >
                                    {terminalDevice.is_virtual ? 'Virtual' : 'Physical'}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}
